// src/lib/briefSections.js
//
// Ticket 072. Pure adapters that turn what the advisor may see into the ONE section shape
// the case brief renders:
//   buildSections(...)        the original brief (Snapshot and/or Calculator), moved out of
//                             CaseBriefPage.js and updated to the Income / Assets layout
//   buildIntakeSections(...)  a saved intake (facts + results). Takes a plain saved-intake
//                             object and does not read consent or contact fields (those come
//                             in as `identity`), so ticket 074 can feed it other clients.
//
// Facts vs conclusions (rule 7): the brief never writes anything. Residency is labelled as
// worked out from the move date. The 1900 "before the cutoff" placeholder is never printed:
// every move-date line goes through isBeforeCutoff / moveBeforeLabel.
//
// Shared labels are imported, not copied: sharedOptions.js (MAJOR_EVENT_OPTIONS,
// MAJOR_EVENT_INCOME_LINKS, expense keys, bands), slotConfig.js, intakeConfig.js.

import React from 'react'
import { getSlotLabel, getSlotReportLabel } from './slotConfig'
import { cleanBasis } from './briefUtils'
import { formatMoveDate } from './briefFormat'
import { getFindingDisplay, getJurisdiction, mapStatus } from './briefTaxWork'
import {
  AGE_BAND_OPTIONS, COUNTRY_OPTIONS, INCOME_CURRENCY_OPTIONS, DEPRECIATION_SOURCE_OPTIONS, EMPLOYMENT_WORK_LOCATION_OPTIONS, INCOME_PERIOD_OPTIONS,
  MAJOR_EVENT_INCOME_LINKS, MAJOR_EVENT_OPTIONS, PROPERTY_LOCATION_OPTIONS,
  AUTONOMO_TYPE_OPTIONS, RENTAL_EXPENSE_KEYS, BUSINESS_EXPENSE_KEYS,
} from './sharedOptions'
import { answersFromFacts23 } from './intake/section23Facts'
import { isBeforeCutoff } from './intake/section1Facts'
import { moveBeforeLabel, RESULTS_NO_INCOME_REASON, RESULTS_ENGINE_BLOCKED_REASON, OWNED_SPAIN_LABEL, OWNED_OUTSIDE_LABEL, REVIEW_NOT_ANSWERED, REVIEW_NOT_SURE } from './intake/intakeConfig'
import { buildIntakeGaps, intakeFooterLine } from './advisorIntakeModel'
import { blockedReasons } from './intake/resultsModel'

// ── Events: which side of the brief, and whether an income card already says it ─────────
// Left = Income side, right = Assets and uncommon events side (D2). One constant; a test
// fails if a MAJOR_EVENT_OPTIONS value has no side.
export const BRIEF_EVENT_SIDES = {
  worked_for_employer:         'income',
  self_employed:               'income',
  received_rental_income:      'income',
  received_dividends_interest: 'income',
  received_pension_income:     'income',
  retirement_withdrawal:       'income',
  rsu_or_options:              'uncommon',
  owned_business:              'uncommon',
  sold_property:               'uncommon',
  large_investment_sale:       'uncommon',
  sold_crypto:                 'uncommon',
  inheritance_or_gift:         'uncommon',
  made_significant_gift:       'uncommon',
}
// RSUs show on the right always and ALSO on the left when there is no employment card.
export const BRIEF_EVENT_ALSO_ON_INCOME_SIDE = ['rsu_or_options']

const eventOption = (value) => MAJOR_EVENT_OPTIONS.find((o) => o.value === value)

// Pure. `covers` data lives in MAJOR_EVENT_INCOME_LINKS (shared options). Is this event
// already said by one of the income cards? items: [{ income_type, income_subtype }].
export function eventIsCovered(event, items) {
  const link = MAJOR_EVENT_INCOME_LINKS.find((l) => l.event === event)
  if (!link || link.covers === 'never') return false
  return (items || []).some((i) => {
    if (i.income_type !== link.income_type) return false
    if (link.covers === 'type') return true
    return !!i.income_subtype && link.income_subtypes.includes(i.income_subtype)
  })
}

// Ticked events (values) that no income card covers. Events with no link row (inheritance,
// owned a business, gifts) are never covered, so they always come back.
export function uncoveredEvents(ticked, items) {
  return (ticked || []).filter((e) => !eventIsCovered(e, items))
}

// ticked: event values. items: income cards [{ income_type, income_subtype }]. hasCards:
// whether the "no income entered" claim may be made (D4: only when cards exist).
// label(value): the wording. soldPropertyIn: text for "(in Spain)" or null.
export function splitEvents({ ticked, items, hasCards, label, soldPropertyIn = null }) {
  const left = []
  const right = []
  for (const e of ticked || []) {
    const side = BRIEF_EVENT_SIDES[e]
    const covered = eventIsCovered(e, items)
    if (!covered && side === 'income') {
      left.push({ key: e, label: label(e) })
    }
    if (!covered && side === 'uncommon') {
      const text = e === 'sold_property' && soldPropertyIn ? `${label(e)} (${soldPropertyIn})` : label(e)
      right.push({ key: e, label: text })
    }
    if (BRIEF_EVENT_ALSO_ON_INCOME_SIDE.includes(e) && !(items || []).some((i) => i.income_type === 'employment')) {
      left.push({ key: `${e}:income`, label: label(e) })
    }
  }
  return { left, right }
}

// "🇪🇸 Spain" -> "Spain" (option labels carry a flag; the sentence reads better without)
const plain = (s) => String(s || '').replace(/^[^\p{L}\p{N}]+/u, '').trim()

// ── Family status (two modes) ────────────────────────────────────────────────────────────
// 'calculator': the Calculator form cannot tell a typed 0 from a default, so zero counts
//   are omitted (old rule). 'intake': zeros are confirmed answers and "not answered" /
//   "not sure" are said. A count is { state: 'value'|'zero'|'unknown'|'open', n }.
export function buildFamilyStatus({ marital, children, under3, over65, over75, disability, mode }) {
  const parts = []
  if (marital) parts.push(getSlotLabel('marital_status', marital))
  const num = (c) => (c && c.state === 'value' ? c.n : (c && c.state === 'zero' ? 0 : null))
  const noun = (n, one, many) => `${n} ${n === 1 ? one : many}`

  if (mode === 'calculator') {
    const c = num(children)
    const d = num(over65)
    if (c > 0) parts.push(noun(c, 'child', 'children'))
    if (d > 0) parts.push(`${d} 65+ ${d === 1 ? 'dependent' : 'dependents'}`)
    return parts
  }

  const count = (c, one, many, sub, subWord) => {
    if (!c || c.state === 'open') return `${many}: ${REVIEW_NOT_ANSWERED.toLowerCase()}`
    if (c.state === 'unknown') return `${many}: ${REVIEW_NOT_SURE.toLowerCase()}`
    const n = num(c)
    let text = noun(n, one, many)
    const s = num(sub)
    if (n > 0 && s > 0) text += ` (${s} ${subWord})`
    return text
  }
  // Confirmed zero children AND zero 65+ dependents read as one "No dependents".
  if (children && children.state === 'zero' && over65 && over65.state === 'zero') {
    parts.push('No dependents')
  } else {
    parts.push(count(children, 'child', 'children', under3, 'under 3'))
    const dep = (() => {
      if (!over65 || over65.state === 'open') return '65+ dependents: not answered'
      if (over65.state === 'unknown') return '65+ dependents: not sure'
      const n = num(over65)
      let text = `${n} 65+ ${n === 1 ? 'dependent' : 'dependents'}`
      const s2 = num(over75)
      if (n > 0 && s2 > 0) text += ` (${s2} aged 75+)`
      return text
    })()
    parts.push(dep)
  }
  if (disability === true) parts.push('Disability')
  else if (disability === false) parts.push('No disability')
  else if (disability === 'unknown') parts.push('Disability: not sure')
  else parts.push('Disability: not answered')
  return parts
}

// ── Shared: Tax work (Setup / Spain filings / Unconfirmed) ───────────────────────────────
// Moved unchanged from the old buildSections. `beckhamNotActive` adds the "Not enrolled" setup row.
export function buildTaxWork(allFindings, dataGaps, beckhamNotActive) {
  const UPFRONT_IDS = new Set(['autonomo_setup', 'first_time_filer', 'autonomo'])
  const UPFRONT_ACTION_TEXT = {
    autonomo_setup:   'Register with RETA (Social Security) + AEAT before starting work',
    first_time_filer: 'Needs NIE + AEAT registration',
  }
  const setupTasks = allFindings
    .filter(f => UPFRONT_IDS.has(f.id) && f.applies === 'yes')
    .map(f => ({ label: getFindingDisplay(f).line1, value: UPFRONT_ACTION_TEXT[f.id] || cleanBasis(f.basis) }))

  if (beckhamNotActive) {
    setupTasks.push({
      label: 'Beckham Law',
      value: 'Not enrolled — confirm eligibility if recently arrived',
    })
  }

  const EXCLUDED_FROM_SPAIN_FILINGS = new Set([...UPFRONT_IDS, 'capital_gains'])
  const spainFindings = allFindings
    .filter(f => !EXCLUDED_FROM_SPAIN_FILINGS.has(f.id))
    .filter(f => { const j = getJurisdiction(f); return j === 'spain' || j === 'both' })
    .sort((a, b) => ({ yes: 0, possible: 1, unknown: 2, no: 3 }[a.applies] ?? 3) - ({ yes: 0, possible: 1, unknown: 2, no: 3 }[b.applies] ?? 3))
    .map(f => ({ ...getFindingDisplay(f), status: mapStatus(f.applies) }))

  const unconfirmedFacts = (dataGaps || []).filter(g => g.jurisdiction === 'spain' || g.jurisdiction === 'both')
  return { setupTasks, spainFindings, unconfirmedFacts }
}

// Income by owner (self/spouse when a spouse item exists), then by Spanish tax base.
function groupByBase(items) {
  const order = ['general', 'savings', 'excluded', 'uncertain']
  const buckets = {}
  items.forEach(item => {
    const key = item.spanish_tax_base || 'uncertain'
    ;(buckets[key] = buckets[key] || []).push(item)
  })
  return order.filter(k => buckets[k]?.length > 0).map(k => ({ base: k, items: buckets[k] }))
}

export function groupIncomeByOwner(incomeItems) {
  if (!incomeItems || incomeItems.length === 0) return null
  const hasSpouseItems = incomeItems.some(i => i.owner === 'spouse')
  if (!hasSpouseItems) return [{ owner: 'self', label: null, groups: groupByBase(incomeItems) }]
  const selfItems   = incomeItems.filter(i => (i.owner || 'self') === 'self')
  const spouseItems = incomeItems.filter(i => i.owner === 'spouse')
  return [
    selfItems.length > 0   ? { owner: 'self',   label: 'You',    groups: groupByBase(selfItems) }   : null,
    spouseItems.length > 0 ? { owner: 'spouse', label: 'Spouse', groups: groupByBase(spouseItems) } : null,
  ].filter(Boolean)
}

// Renders a multi-select fact as a plain label string when there's exactly one value, or a
// real <ul> when there's more than one. Shared by Real estate holdings (old brief).
function buildFactListValue(items, slotKey) {
  const labels = items.map(v => ({ v, label: getSlotReportLabel(slotKey, v) || v }))
  if (labels.length <= 1) return labels[0]?.label ?? null
  return (
    <ul className="brief-fact-list">
      {labels.map(({ v, label }) => <li key={v}>{label}</li>)}
    </ul>
  )
}

// ── The original brief (Snapshot and/or Calculator) ─────────────────────────────────────

export function buildSections(brief, subject, tp, yp, email, phone, triagePublicToken) {
  const taxYear = brief.tax_year
  const s       = brief.subject || {}

  const comunidad = s.comunidad || tp?.comunidad_autonoma
  const products  = subject.products || { triage: false, calculator: false }
  const allFindings = brief.obligations || []

  const filingHistoryParts = [
    yp?.filed_spain_before != null ? (yp.filed_spain_before ? 'Prior Spain filing' : 'No prior Spain filing') : null,
    yp?.has_gestor != null ? (yp.has_gestor ? 'Used a gestor before' : 'No prior gestor') : null,
    yp?.filing_goal ? getSlotReportLabel('filing_goal', yp.filing_goal) : null,
  ].filter(Boolean)

  const calc = brief.calculator_estimate || null

  const maritalStatus     = s.marital_status || tp?.marital_status
  const citizenshipStatus = s.citizenship_status || tp?.citizenship_status

  // Calculator zeros cannot be told from defaults: omit-zero mode, and only when a
  // calculator run exists (calculator-only data, no fallback source).
  const asCount = (n) => (n == null ? { state: 'open' } : (n === 0 ? { state: 'zero' } : { state: 'value', n }))
  const familyStatusParts = buildFamilyStatus({
    marital: maritalStatus,
    children: calc ? asCount(calc.dependents?.children_under_25 ?? 0) : null,
    over65:   calc ? asCount(calc.dependents?.ascendants_over_65 ?? 0) : null,
    mode: 'calculator',
  })

  const header = {
    name:        s.display_name || subject.display_name,
    email,
    phone,
    triagePublicToken,
    leadStatus:  subject.lead_status || 'new',
    assignedAt:  subject.lead_assigned_at,
    summaryRows: [
      taxYear ? { label: 'Tax year', value: String(taxYear) } : null,
      comunidad ? { label: 'Comunidad Autónoma', value: getSlotLabel('comunidad_autonoma', comunidad) } : null,
      (yp?.spain_residency_status || s?.residency_status || s.move_date || tp?.move_date)
        ? { label: `Spain residency (${taxYear})`, value: [
            (yp?.spain_residency_status || s?.residency_status) ? getSlotReportLabel('spain_residency_status', yp?.spain_residency_status || s?.residency_status) : null,
            (s.move_date || tp?.move_date) ? `Arrived ${formatMoveDate(s.move_date || tp?.move_date)}` : null,
          ].filter(Boolean).join(' · ') }
        : null,
      citizenshipStatus
        ? { label: 'Citizenship', value: getSlotLabel('citizenship_status', citizenshipStatus) }
        : null,
      familyStatusParts.length > 0
        ? { label: 'Family status', value: familyStatusParts.join(' · ') }
        : null,
      (s.beckham_active || tp?.beckham_status)
        ? { label: 'Beckham Law', value: s.beckham_active ? 'Active / approved' : getSlotReportLabel('beckham_status', tp?.beckham_status) }
        : null,
      filingHistoryParts.length > 0
        ? { label: 'Filing history', value: filingHistoryParts.join(' · ') }
        : null,
    ].filter(Boolean),
  }

  // Income facts — only rendered when there's no calculator run to supersede them.
  const incomeFacts = (yp?.income_types || [])
    .filter(t => t !== 'none')
    .map(t => {
      const typeLabel = getSlotReportLabel('income_types', t) || t
      let detail = null
      if (t === 'self_employment' && yp?.self_employment_country) {
        detail = getSlotReportLabel('self_employment_country', yp.self_employment_country)
      } else if (t === 'rental' && yp?.rental_property_location) {
        detail = getSlotReportLabel('rental_property_location', yp.rental_property_location)
      } else if ((t === 'employment_es' || t === 'employment_us') && yp?.employment_location_when_earned) {
        detail = getSlotReportLabel('employment_location_when_earned', yp.employment_location_when_earned)
      } else if (t === 'pension' && yp?.pension_source) {
        detail = [
          getSlotReportLabel('pension_source', yp.pension_source),
          yp?.pension_is_government != null ? getSlotReportLabel('pension_is_government', yp.pension_is_government) : null,
        ].filter(Boolean).join(' · ')
      } else if (t === 'business_partnership' && yp?.business_partnership_type) {
        detail = getSlotReportLabel('business_partnership_type', yp.business_partnership_type)
      } else if (t === 'investments') {
        detail = 'Dividends, interest or capital gains'
      }
      return { label: typeLabel, value: detail || 'Reported' }
    })

  // Assets — Snapshot-exclusive. Major events are no longer one row here: they sit on
  // the Income or the Assets and uncommon events side (events below).
  const assetFacts = [
    yp?.foreign_account_band
      ? { label: 'Foreign accounts / assets', value: getSlotLabel('foreign_account_band', yp.foreign_account_band) }
      : null,
    yp?.worldwide_net_worth_band
      ? { label: 'Estimated net worth', value: getSlotLabel('worldwide_net_worth_band', yp.worldwide_net_worth_band) }
      : null,
    tp?.asset_split && ['married','civil_partnership'].includes(maritalStatus)
      ? { label: 'Asset split', value: getSlotReportLabel('asset_split', tp.asset_split) }
      : null,
    yp?.property_ownership?.length > 0
      ? { label: 'Real estate holdings', value: buildFactListValue(yp.property_ownership, 'property_ownership') }
      : null,
  ].filter(Boolean)

  const incomeCards = (calc?.income_items || []).map(i => ({ income_type: i.income_type, income_subtype: i.income_subtype }))
  const events = splitEvents({
    ticked: yp?.major_events || [],
    items: incomeCards,
    hasCards: incomeCards.length > 0,
    label: (v) => getSlotReportLabel('major_events', v) || eventOption(v)?.label || v,
  })

  const taxWork = buildTaxWork(allFindings, brief.data_gaps || [], s.beckham_status === 'not_active')

  return {
    source: 'brief',
    header,
    products,
    ...taxWork,
    taxWorkComputed: true,
    assetFacts,
    incomeFacts,
    events,
    calculatorEstimate: calc,
    incomeByOwner: groupIncomeByOwner(calc?.income_items),
    filingComparison: calc?.filing_comparison || null,
    foreignTaxes: [],
    dataConflicts: brief.data_conflicts || [],
    advisorNotes: brief.advisor_notes || null,
    intake: null,
  }
}

// ── A saved intake ───────────────────────────────────────────────────────────────────────

const known = (f) => (f && f.status === 'known_value' ? f.value : undefined)
const stateOf = (f) => (f ? f.status : 'unanswered')
const labelOf = (options, value) => {
  const o = options.find((x) => x.value === value)
  return o ? o.label : String(value)
}

function countOf(f) {
  if (!f) return { state: 'open' }
  if (f.status === 'known_zero') return { state: 'zero' }
  if (f.status === 'known_value') return f.value === 0 ? { state: 'zero' } : { state: 'value', n: f.value }
  if (f.status === 'unknown') return { state: 'unknown' }
  return { state: 'open' }
}

function boolOf(f) {
  if (f && f.status === 'known_value') return f.value === true
  if (f && f.status === 'unknown') return 'unknown'
  return null
}

function ageBandText(f) {
  const v = known(f)
  if (v !== undefined) return labelOf(AGE_BAND_OPTIONS, v)
  return stateOf(f) === 'unknown' ? REVIEW_NOT_SURE : REVIEW_NOT_ANSWERED
}

function moneyAs(amount, currency, period) {
  const money = `${Number(amount).toLocaleString('en-US')} ${currency || 'EUR'}`
  const p = INCOME_PERIOD_OPTIONS.find((o) => o.value === period)
  return p ? `${money} ${p.label}` : money
}

const fmtNum = (n) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })
const fmtEuros = (n) => `€${Math.round(Number(n)).toLocaleString('en-US')}`
// "$" for USD, "€" for EUR, "£" for GBP: taken from the shared currency option labels ("USD ($)").
const currencySymbol = (currency) => {
  const o = INCOME_CURRENCY_OPTIONS.find((x) => x.value === (currency || 'EUR'))
  const m = o && /\(([^)]+)\)/.exec(o.label)
  return m ? m[1] : `${currency} `
}
const fmtMoney = (n, currency, sign = '') => `${sign}${currencySymbol(currency)}${fmtNum(n)}`
const unitText = (currency, period) => {
  const p = INCOME_PERIOD_OPTIONS.find((o) => o.value === period)
  return `${currency || 'EUR'}${p ? ` ${p.label}` : ''}`
}

// "1 USD = 0.92 EUR (HolaSam planning rate)": the rate the engine used, shown where the
// amount is converted (ticket 072 feedback), not only in the estimate's footnote.
export function fxText(classified) {
  if (!classified || !classified.currency || classified.currency === 'EUR' || !classified.exchange_rate_used) return null
  // No source label: one system rate is coming in a separate ticket.
  return `1 ${classified.currency} = ${classified.exchange_rate_used} EUR`
}

// Meta lines the original income row never had: work location, as-entered amount, started
// this year, the FX rate. Joined from the client's own facts by item id (W1, W3, W4).
function extraMetaFor(factItem, classified) {
  if (!factItem) return []
  const out = []
  if (factItem.income_type === 'employment') {
    const wl = known(factItem.work_location)
    out.push(wl !== undefined ? plain(labelOf(EMPLOYMENT_WORK_LOCATION_OPTIONS, wl)) : 'Work location: not answered')
  }
  if (factItem.income_type === 'self_employment' && known(factItem.activity_started_this_year) === true) {
    out.push('Started this year')
  }
  const amount = known(factItem.amount)
  if (amount !== undefined && (factItem.currency !== 'EUR' || factItem.period !== 'annual')) {
    out.push(`Entered as ${moneyAs(amount, factItem.currency, factItem.period)}`)
  }
  const fx = fxText(classified)
  if (fx) out.push(`Converted at ${fx}`)
  return out
}

// One block per rental or self-employment card (own inner box). Numbers are AS ENTERED (one
// unit line per block, not repeated per amount); the Total row is the engine's own net in
// that unit and the next line converts it to euros per year, which is the very figure the
// IRPF income table shows (both read classified_items[].net_amount_eur). Foreign tax paid is
// a memo line, not part of the net. Amounts carry their currency symbol. The euro line is left out
// when the card was already entered in euros per year (the Total is then the same number).
function rentalBusinessDetail(factItems, classifiedItems, results) {
  const byId = new Map((classifiedItems || []).map((c) => [c.id, c]))
  const blocks = []
  for (const it of factItems) {
    if (it.income_type !== 'rental' && it.income_type !== 'self_employment') continue
    const isRental = it.income_type === 'rental'
    const c = byId.get(it.id) || null
    const itemized = known(it.expense_path) === 'itemized'
    const keys = isRental ? RENTAL_EXPENSE_KEYS : BUSINESS_EXPENSE_KEYS
    const rows = []
    const amount = known(it.amount)
    if (amount !== undefined) rows.push({ label: itemized ? 'Gross' : 'Net amount entered', value: fmtMoney(amount, it.currency) })
    if (itemized) {
      const expenses = known(it.expenses) || {}
      for (const k of keys) {
        const v = expenses[k.value]
        if (typeof v === 'number' && v > 0) rows.push({ label: k.label, value: fmtMoney(v, it.currency, '−') })
      }
    }
    const dep = known(it.depreciation_amount)
    if (isRental) {
      const src = known(it.depreciation_source)
      const srcText = src !== undefined ? labelOf(DEPRECIATION_SOURCE_OPTIONS, src) : null
      if (typeof dep === 'number' && dep > 0) rows.push({ label: 'Depreciation', value: fmtMoney(dep, it.currency, '−'), source: srcText })
      else rows.push({ label: 'Depreciation', value: 'Not entered', source: srcText })
    }
    const unit = unitText(it.currency, it.period)
    const memo = []
    if (c && c.tax_paid_foreign > 0) memo.push({ label: 'Foreign tax paid', value: fmtMoney(c.tax_paid_foreign, it.currency) })
    else if (c && c.tax_paid_precision === 'unknown') memo.push({ label: 'Foreign tax paid', value: 'unknown' })

    blocks.push({
      id: it.id,
      title: it.label || (isRental ? 'Rental' : 'Self-employment'),
      kind: isRental ? 'rental' : 'business',
      unit,
      rows,
      total: c && c.net_amount_original != null ? { label: `Total (${unit})`, value: fmtMoney(c.net_amount_original, it.currency) } : null,
      euro: c && c.net_amount_eur != null && !(it.currency === 'EUR' && it.period === 'annual') ? { label: 'In euros per year', value: fmtEuros(c.net_amount_eur) } : null,
      fx: fxText(c),
      memo,
    })
  }
  return blocks
}

function ownershipText(property) {
  const a = answersFromFacts23({ property })
  if (a.ownership) {
    const parts = []
    if (a.ownership.spain) parts.push(OWNED_SPAIN_LABEL)
    if (a.ownership.outside) {
      const names = (a.ownership.countries || []).map((c) => labelOf(COUNTRY_OPTIONS, c))
      parts.push(names.length ? `${OWNED_OUTSIDE_LABEL}: ${names.join(', ')}` : OWNED_OUTSIDE_LABEL)
    }
    return parts.join('; ')
  }
  const spain = property && property.owns_spain_property
  if (spain && spain.status === 'known_value' && spain.value === false) return 'None'
  return null
}

// The advisor's Autónomo card (W8): the client's answers and labelled engine estimates.
function autonomoCard(facts, results) {
  const autonomo = facts.income && facts.income.autonomo
  const hasOwnSelfEmployment = ((facts.income && facts.income.items) || []).some((i) => i.income_type === 'self_employment' && (i.owner || 'self') === 'self')
  if (!autonomo || !hasOwnSelfEmployment) return null
  const answers = []
  const type = known(autonomo.autonomo_type)
  answers.push({ label: 'Type', value: type !== undefined ? labelOf(AUTONOMO_TYPE_OPTIONS, type) : REVIEW_NOT_ANSWERED })
  const reta = known(autonomo.registered_reta_last_2_years)
  answers.push({ label: 'Registered in RETA in the last 2 years', value: reta === undefined ? REVIEW_NOT_ANSWERED : (reta ? 'Yes' : 'No') })
  if (reta === false) {
    const tp = known(autonomo.previously_used_tarifa_plana)
    answers.push({ label: 'Previously used the tarifa plana', value: tp === undefined ? REVIEW_NOT_ANSWERED : (tp ? 'Yes' : 'No') })
  }
  const estimates = []
  const r = results.autonomo && results.autonomo.result
  if (r) {
    if (r.effectiveMonthlyReta != null) {
      const band = r.reta && r.reta.band && r.reta.band.band_label
      estimates.push({ label: 'Monthly RETA', value: `€${Math.round(r.effectiveMonthlyReta).toLocaleString('en-US')}/month${band ? ` (band ${band})` : ''}` })
    }
    const m130 = r.modelo130
    if (m130 && m130.perActivity && m130.perActivity.length > 0) {
      estimates.push({ label: 'Modelo 130', value: `€${Math.round(m130.totalEstimatedQuarterlyPayment).toLocaleString('en-US')}/quarter` })
    }
  }
  return { answers, estimates }
}

function noEstimateReasons(results, comunidad) {
  const u = results.irpf_unavailable
  if (results.irpf || !u) return []
  if (u.reason === 'eligibility_blocked') return blockedReasons(u.codes, comunidad)
  if (u.reason === 'no_income_with_amount') return [RESULTS_NO_INCOME_REASON]
  return [RESULTS_ENGINE_BLOCKED_REASON]
}

// identity: { name, email, phone, leadStatus, assignedAt, triagePublicToken? } (the page's
// subject; this builder reads nothing about consent). today: the saved date, so "before {year}" matches the moment the client saved.
export function buildIntakeSections(intake, identity = {}) {
  const facts = intake.facts || {}
  const results = intake.results || {}
  const household = facts.household || {}
  const savedAt = intake.saved_at ? new Date(intake.saved_at) : new Date()
  const today = Number.isNaN(savedAt.getTime()) ? new Date() : savedAt
  const snapshot = results.snapshot || {}
  const derived = snapshot.derived_facts || {}
  const factItems = (facts.income && facts.income.items) || []
  const taxYear = intake.tax_year

  // ── Header ──────────────────────────────────────────────────────────────────────────
  const comunidad = known(household.comunidad_autonoma)
  const moveDate = known(household.move_date)
  const moveText = moveDate === undefined ? null
    : (isBeforeCutoff(moveDate, today) ? moveBeforeLabel(today) : `Arrived ${formatMoveDate(moveDate)}`)
  const residency = derived.spainResidencyStatus
    ? `${getSlotReportLabel('spain_residency_status', derived.spainResidencyStatus)} (based on move date)`
    : null
  const citizenship = known(household.citizenship_status)
  const marital = known(household.marital_status)
  const hasSpouse = household.spouse_citizenship_status || household.spouse_eu_citizen || household.spouse_age_band

  const family = buildFamilyStatus({
    marital,
    children: countOf(household.children_under_25),
    under3: countOf(household.children_under_3),
    over65: countOf(household.ascendants_over_65),
    over75: countOf(household.ascendants_over_75),
    disability: boolOf(household.disability_taxpayer),
    mode: 'intake',
  })

  // "Age + Citizenship" (ticket 072 feedback): age first, then citizenship, then EU status.
  const spouseParts = []
  const sa = household.spouse_age_band
  if (sa && sa.status !== 'not_applicable') {
    spouseParts.push(known(sa) !== undefined ? ageBandText(sa) : `Age: ${ageBandText(sa).toLowerCase()}`)
  }
  const sc = known(household.spouse_citizenship_status)
  if (household.spouse_citizenship_status && household.spouse_citizenship_status.status !== 'not_applicable') {
    spouseParts.push(sc !== undefined ? getSlotLabel('citizenship_status', sc) : (stateOf(household.spouse_citizenship_status) === 'unknown' ? `Citizenship: ${REVIEW_NOT_SURE.toLowerCase()}` : `Citizenship: ${REVIEW_NOT_ANSWERED.toLowerCase()}`))
  }
  const se = household.spouse_eu_citizen
  if (se && se.status !== 'not_applicable') {
    spouseParts.push(known(se) !== undefined ? `EU citizen: ${known(se) ? 'yes' : 'no'}` : (se.status === 'unknown' ? `EU citizen: ${REVIEW_NOT_SURE.toLowerCase()}` : `EU citizen: ${REVIEW_NOT_ANSWERED.toLowerCase()}`))
  }

  const ageText = known(household.age_band) !== undefined ? ageBandText(household.age_band) : `Age: ${ageBandText(household.age_band).toLowerCase()}`
  const ageCitizenship = [ageText, citizenship ? getSlotLabel('citizenship_status', citizenship) : null].filter(Boolean).join(' · ')

  const beckham = known(household.beckham_status)
  const filing = facts.filing_history || {}
  const filedBefore = known(filing.filed_spain_before)
  const gestor = known(filing.has_gestor)
  const filingHistoryParts = [
    filedBefore !== undefined ? (filedBefore ? 'Prior Spain filing' : 'No prior Spain filing') : null,
    gestor !== undefined ? (gestor ? 'Used a gestor before' : 'No prior gestor') : null,
  ].filter(Boolean)

  const header = {
    name: identity.name,
    email: identity.email,
    phone: identity.phone,
    leadStatus: identity.leadStatus || 'new',
    assignedAt: identity.assignedAt,
    footerLine: intakeFooterLine(intake),
    summaryRows: [
      taxYear ? { label: 'Tax year', value: String(taxYear) } : null,
      comunidad ? { label: 'Comunidad Autónoma', value: getSlotLabel('comunidad_autonoma', comunidad) } : null,
      (residency || moveText) ? { label: `Spain residency (${taxYear})`, value: [residency, moveText].filter(Boolean).join(' · ') } : null,
      { label: 'Age + Citizenship', value: ageCitizenship },
      hasSpouse && spouseParts.length > 0 ? { label: 'Spouse', value: spouseParts.join(' · ') } : null,
      family.length > 0 ? { label: 'Family status', value: family.join(' · ') } : null,
      beckham ? { label: 'Beckham Law', value: getSlotReportLabel('beckham_status', beckham) } : null,
      filingHistoryParts.length > 0 ? { label: 'Filing history', value: filingHistoryParts.join(' · ') } : null,
    ].filter(Boolean),
  }

  // ── Income (from the frozen IRPF result) ────────────────────────────────────────────
  const irpf = results.irpf || null
  const result = irpf && irpf.result
  let calculatorEstimate = null
  let incomeByOwner = null
  let filingComparison = null
  let foreignTaxes = []
  if (result) {
    const byId = new Map(factItems.map((i) => [i.id, i]))
    const incomeItems = (result.classified_items || []).map((item) => ({
      label: item.label || item.income_type || 'Income item',
      income_type: item.income_type || '',
      income_subtype: item.income_subtype ?? null,
      spanish_tax_base: item.spanish_tax_base || 'uncertain',
      amount_eur: item.net_amount_eur ?? null,
      review_flags: item.review_flags || [],
      source_country: item.source_country ?? null,
      // Foreign taxes are listed once in their own group (foreignTaxes below), not on each row.
      tax_paid_foreign: 0,
      tax_withheld_spain: item.tax_withheld_spain ?? 0,
      tax_paid_precision: item.tax_paid_precision || 'unknown',
      activity_classification: item.activity_classification ?? null,
      owner: item.owner === 'spouse' ? 'spouse' : 'self',
      extra_meta: extraMetaFor(byId.get(item.id), item),
    }))
    const range = result.estimate_range
    calculatorEstimate = {
      is_draft: false,
      tax_year: results.meta && results.meta.irpf_rate_year,
      irpf_gross: result.irpf_gross ?? null,
      effective_rate: result.effective_rate_on_total_income ?? null,
      estimate_low: range ? Math.round(range.low) : null,
      estimate_high: range ? Math.round(range.high) : null,
      display_mode: result.display_mode ?? null,
      adjustment_items: result.display_mode === 'adjustment_estimate' ? (result.adjustment_items || []).map((a) => ({
        label: a.label || '', condition: a.condition || '', potential_reduction: a.potential_reduction ?? 0, advisor_note: a.advisor_note || '',
      })) : null,
      income_items: incomeItems,
    }
    incomeByOwner = groupIncomeByOwner(incomeItems)
    foreignTaxes = (result.classified_items || [])
      .filter((item) => item.tax_paid_foreign > 0)
      .map((item) => ({ key: item.id, label: item.label || item.income_type, value: `${fmtNum(item.tax_paid_foreign)} ${item.currency || 'EUR'}` }))
    const fc = irpf.filing_comparison
    if (fc) {
      filingComparison = {
        recommended_mode: fc.recommended_mode || '',
        household_tax_individual: fc.household_tax_individual ?? 0,
        household_tax_joint: fc.household_tax_joint ?? 0,
        savings_vs_alternative: fc.savings_vs_alternative ?? 0,
        self_irpf_gross: fc.self_result ? fc.self_result.irpf_gross ?? null : null,
        spouse_irpf_gross: fc.spouse_result ? fc.spouse_result.irpf_gross ?? null : null,
        assumptions: fc.assumptions || [],
      }
    }
  }

  // ── Assets and events ───────────────────────────────────────────────────────────────
  const assets = facts.assets || {}
  const bandText = (f, slot) => {
    const v = known(f)
    if (v !== undefined) return getSlotLabel(slot, v)
    return stateOf(f) === 'unknown' ? REVIEW_NOT_SURE : null
  }
  const fa = bandText(assets.foreign_account_band, 'foreign_account_band')
  const nw = bandText(assets.worldwide_net_worth_band, 'worldwide_net_worth_band')
  const own = ownershipText(facts.property)
  const assetFacts = [
    fa ? { label: 'Foreign accounts / assets', value: fa } : null,
    nw ? { label: 'Estimated net worth', value: nw } : null,
    known(household.asset_split) !== undefined ? { label: 'Asset split', value: getSlotReportLabel('asset_split', known(household.asset_split)) } : null,
    own ? { label: 'Real estate holdings', value: own } : null,
  ].filter(Boolean)

  const eventAnswers = answersFromFacts23({ events: facts.events }).events
  const ticked = eventAnswers ? MAJOR_EVENT_OPTIONS.filter((o) => eventAnswers[o.value] === true).map((o) => o.value) : []
  const sale = known((facts.property || {}).sale_location)
  const cards = factItems.map((i) => ({ income_type: i.income_type, income_subtype: known(i.income_subtype) }))
  const events = splitEvents({
    ticked,
    items: cards,
    hasCards: cards.length > 0,
    label: (v) => eventOption(v)?.label || v,
    soldPropertyIn: sale ? `in ${plain(labelOf(PROPERTY_LOCATION_OPTIONS, sale)).replace(/^Another country$/, 'another country')}` : null,
  })

  // ── Tax work (frozen at save; absent on versions saved before engine 4) ──────────────
  const taxWorkComputed = Array.isArray(results.obligations)
  const taxWork = buildTaxWork(
    taxWorkComputed ? results.obligations : [],
    Array.isArray(results.data_gaps) ? results.data_gaps : [],
    beckham === 'not_active',
  )

  const caveats = result
    ? [...new Set([...(result.assumptions || []), ...(result.warnings || [])])]
    : []

  return {
    source: 'intake',
    header,
    products: { triage: false, calculator: false },
    ...taxWork,
    taxWorkComputed,
    assetFacts,
    incomeFacts: [],
    events,
    calculatorEstimate,
    incomeByOwner,
    filingComparison,
    foreignTaxes,
    dataConflicts: [],
    advisorNotes: null,
    intake: {
      rateYear: results.meta && results.meta.irpf_rate_year,
      noEstimateReasons: noEstimateReasons(results, comunidad),
      gaps: buildIntakeGaps(intake, today),
      rentalBusiness: rentalBusinessDetail(factItems, result ? result.classified_items : [], results),
      autonomo: autonomoCard(facts, results),
      caveats,
    },
  }
}
